import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { ArrowLeft, RefreshCw, CheckCircle, XCircle, Clock, ExternalLink } from 'lucide-react';
import { collection, query, orderBy, limit, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { formatDate } from '@/lib/utils';

interface WebhookLog {
  id: string;
  eventId: string;
  eventType: string;
  status: 'processing' | 'success' | 'error';
  metadata?: Record<string, any>;
  enrollmentId?: string;
  error?: string;
  createdAt: Date;
  processedAt: Date;
}

export default function StripeWebhooks() {
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const { toast } = useToast();

  const [webhooks, setWebhooks] = useState<WebhookLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Redirect if not admin
  useEffect(() => {
    if (role && role !== 'admin') {
      navigate('/dashboard');
      toast({
        title: 'Access Denied',
        description: 'You need admin privileges to access this page.',
        variant: 'destructive',
      });
    }
  }, [role, navigate, toast]);

  // Load webhook logs
  const loadWebhooks = async () => {
    if (!user || role !== 'admin') {
      return;
    }

    try {
      setRefreshing(true);

      const q = query(
        collection(db, 'webhookLogs'),
        orderBy('createdAt', 'desc'),
        limit(50)
      );

      const querySnapshot = await getDocs(q);

      const webhookData = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          ...data,
          id: doc.id,
          createdAt: (data.createdAt as Timestamp)?.toDate() || new Date(),
          processedAt: (data.processedAt as Timestamp)?.toDate() || new Date(),
        } as WebhookLog;
      });

      setWebhooks(webhookData);
    } catch (error) {
      console.error('[StripeWebhooks] Error loading webhooks:', error);
      toast({
        title: 'Error',
        description: 'Failed to load webhook logs. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadWebhooks();
  }, [user, role]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'processing':
        return <Clock className="w-5 h-5 text-yellow-500 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success':
        return 'bg-green-100 text-green-800';
      case 'error':
        return 'bg-red-100 text-red-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getEventTypeLabel = (eventType: string) => {
    const labels: Record<string, string> = {
      'checkout.session.completed': 'Payment Completed',
      'checkout.session.expired': 'Checkout Expired',
      'payment_intent.payment_failed': 'Payment Failed',
    };
    return labels[eventType] || eventType;
  };

  const getEventTypeColor = (eventType: string) => {
    const colors: Record<string, string> = {
      'checkout.session.completed': 'bg-blue-100 text-blue-800',
      'checkout.session.expired': 'bg-orange-100 text-orange-800',
      'payment_intent.payment_failed': 'bg-red-100 text-red-800',
    };
    return colors[eventType] || 'bg-gray-100 text-gray-800';
  };

  const stats = {
    total: webhooks.length,
    success: webhooks.filter((w) => w.status === 'success').length,
    error: webhooks.filter((w) => w.status === 'error').length,
    processing: webhooks.filter((w) => w.status === 'processing').length,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading webhook logs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/route-offers')}
            className="mb-4 flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Route Offers
          </Button>
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Stripe Webhooks</h1>
              <p className="text-gray-600 mt-1">
                Monitor webhook events and payment processing
              </p>
            </div>
            <Button
              onClick={loadWebhooks}
              disabled={refreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-sm text-gray-600">Total Events</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-green-600">{stats.success}</div>
              <div className="text-sm text-gray-600">Successful</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-red-600">{stats.error}</div>
              <div className="text-sm text-gray-600">Errors</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <div className="text-2xl font-bold text-yellow-600">{stats.processing}</div>
              <div className="text-sm text-gray-600">Processing</div>
            </CardContent>
          </Card>
        </div>

        {/* Webhook Configuration Info */}
        <Card className="mb-6 bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-lg">Webhook Configuration</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div>
              <span className="font-medium">Endpoint URL:</span>{' '}
              <code className="bg-blue-100 px-2 py-1 rounded text-xs">
                https://us-central1-sch-summerside-staging.cloudfunctions.net/stripeWebhook
              </code>
            </div>
            <div>
              <span className="font-medium">Events:</span>{' '}
              <span className="text-gray-700">
                checkout.session.completed, checkout.session.expired, payment_intent.payment_failed
              </span>
            </div>
            <div className="pt-2 border-t border-blue-200">
              <p className="text-xs text-gray-600">
                Configure this webhook in your{' '}
                <a
                  href="https://dashboard.stripe.com/webhooks"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Stripe Dashboard
                </a>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Webhook Logs */}
        {webhooks.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="text-gray-400 mb-4">
                <Clock className="w-16 h-16 mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No webhook events yet</h3>
              <p className="text-gray-600">
                Webhook events will appear here when customers make payments.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {webhooks.map((webhook) => (
              <Card key={webhook.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Status Icon */}
                    <div className="flex-shrink-0 mt-1">
                      {getStatusIcon(webhook.status)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEventTypeColor(webhook.eventType)}`}>
                          {getEventTypeLabel(webhook.eventType)}
                        </span>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(webhook.status)}`}>
                          {webhook.status}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatDate(webhook.createdAt)}
                        </span>
                      </div>

                      {/* Metadata */}
                      {webhook.metadata && (
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm text-gray-600 mb-2">
                          {webhook.metadata.offerId && (
                            <div>
                              <span className="font-medium">Offer ID:</span>{' '}
                              <span className="text-xs font-mono">{webhook.metadata.offerId.slice(0, 8)}...</span>
                            </div>
                          )}
                          {webhook.metadata.recipientEmail && (
                            <div>
                              <span className="font-medium">Customer:</span>{' '}
                              {webhook.metadata.recipientEmail}
                            </div>
                          )}
                          {webhook.metadata.totalAmount && (
                            <div>
                              <span className="font-medium">Amount:</span> ${webhook.metadata.totalAmount}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Error Message */}
                      {webhook.error && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          <span className="font-medium">Error:</span> {webhook.error}
                        </div>
                      )}

                      {/* Links */}
                      <div className="flex items-center gap-3 mt-3">
                        {webhook.enrollmentId && (
                          <Link
                            to={`/admin/enrollments/${webhook.enrollmentId}`}
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Enrollment
                          </Link>
                        )}
                        {webhook.metadata?.offerId && (
                          <Link
                            to={`/admin/route-offers/${webhook.metadata.offerId}`}
                            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Offer
                          </Link>
                        )}
                        <span className="text-xs text-gray-500">
                          Event ID: {webhook.eventId}
                        </span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
